import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import Turnstile, { useTurnstile } from 'react-turnstile';
import TextField from '../FormElements/ReactHookForm/TextField';
import { LoadingNotch } from '../../utilities/Loaders';
import DropdownField from '../FormElements/ReactHookForm/DropdownField';
import FieldErrorMessage from '../FormElements/FieldErrorMessage';
import FieldLabel from '../FormElements/FieldLabel';
import RadioOptionField from '../FormElements/ReactHookForm/RadioOptionField';
import {
    nearestLocation,
    howToInvest,
    howMuchToInvest,
    investedBefore,
    enquiryType,
    ENQUIRY_TYPE_GENERAL_ENQUIRY_VALUE,
    HOW_TO_INVEST_AS_AN_INDIVIDUAL_VALUE,
} from './EnquiryFormOptions';
import PhoneNumberField from '../FormElements/ReactHookForm/PhoneNumberField';
import enquiries from '../../client-api/modules/Enquiries';
import { log } from '../../../primary/utilities/bugsnag';
import {
    ENQUIRY_TYPE_FIELD,
    INVESTMENT_METHOD_FIELD,
    NEAREST_LOCATION_FIELD,
    EMAIL_FIELD,
    FIRST_NAME_FIELD,
    INVESTED_BEFORE_FIELD,
    INVESTMENT_AMOUNT_FIELD,
    LAST_NAME_FIELD,
    MESSAGE_FIELD,
    PHONE_NUMBER_FIELD,
    PREFERRED_CONTACT_METHOD_FIELD,
    initialFormValues,
} from './EnquiryFields';
import validationSchema from './EnquiryValidation';

const EnquiryForm = () => {
    const [submitError, setSubmitError] = useState(null);
    const [turnstileToken, setTurnstileToken] = useState(null);

    const methods = useForm({
        defaultValues: initialFormValues,
        resolver: yupResolver(validationSchema),
        shouldFocusError: false,
    });

    const turnstile = useTurnstile();

    const {
        register,
        handleSubmit,
        control,
        watch,
        getValues,
        setValue,
        clearErrors,
        formState: {
            errors,
            isSubmitting,
            isSubmitSuccessful,
        },
    } = methods;

    const isGeneralEnquiry = () => watch(ENQUIRY_TYPE_FIELD) === ENQUIRY_TYPE_GENERAL_ENQUIRY_VALUE;

    const onSubmit = async (data) => {
        setSubmitError(null);
        const body = { ...data, [PHONE_NUMBER_FIELD]: `+${data[PHONE_NUMBER_FIELD]}`, turnstile: turnstileToken };

        // if general enquiry form need to delete extra properties on submit as
        // api will not accept extra fields
        if (body[ENQUIRY_TYPE_FIELD] === ENQUIRY_TYPE_GENERAL_ENQUIRY_VALUE) {
            delete body[INVESTMENT_METHOD_FIELD];
            delete body[INVESTED_BEFORE_FIELD];
            delete body[INVESTMENT_AMOUNT_FIELD];
        }

        try {
            await enquiries.saveEnquiry(body);
        } catch (error) {
            log({ error });

            setSubmitError(error?.message ?? error);
        }
    };

    const ErrorMessage = ({ field }) => <FieldErrorMessage message={errors[field]?.message}/>;

    const scrollToError = (errorFields) => {
        const errorId = Object.keys(errorFields)[0];
        const errorField = errorId === PHONE_NUMBER_FIELD ? '.react-tel-input' : `#${errorId}`;
        const errorFieldPosition = document.querySelector(errorField)?.getBoundingClientRect().top;

        if (!errorFieldPosition) {
            return;
        }

        window.scrollTo({
            top: (errorFieldPosition + window.scrollY) - 40,
            behavior: 'smooth',
        });
    };

    // render success state here
    if (isSubmitSuccessful && !submitError) {
        const isIndividual = () => getValues(INVESTMENT_METHOD_FIELD) === HOW_TO_INVEST_AS_AN_INDIVIDUAL_VALUE;
        const enquiryForm = document.querySelector('.enquiry-form')?.getBoundingClientRect().top;

        window.scrollTo({
            top: (enquiryForm + window.scrollY) - 50,
            behavior: 'smooth',
        });

        return (
            <div className={'success-message'}>
                <h2 className={'pb-3'}>
                    Thank you for getting in touch
                </h2>
                <p>
                    A member of the Forsyth Barr team will respond to your enquiry in one to two business days.
                </p>
                {isIndividual() && (
                    <>
                        <p>
                            As you are investing as an individual you can start your application from the email we’ve
                            just sent you, or you may wish to start an application online now.
                        </p>
                        <a
                            href={`https://eonboard.forsythbarr.co.nz/join?email=${getValues(EMAIL_FIELD)}`}
                            className={'text-uppercase btn btn-primary mt-4'}
                        >Start application</a>
                    </>
                )}
            </div>
        );
    }

    return (
        <>
            <p className={'light-text mb-5'}>
                If you’re interested in investing with us, or you just have a general enquiry,
                complete the form below and we’ll be in touch.
            </p>

            <FormProvider {...methods}>
                <form className={'mobile-number-form'} onSubmit={
                    handleSubmit(
                        async (data) => onSubmit(data),
                        (errorFields) => { scrollToError(errorFields); },
                    )}>
                    <div className={'pb-4 enquiry-form-selector'}>
                        <DropdownField
                            name={ENQUIRY_TYPE_FIELD}
                            options={enquiryType}
                            placeholder={'Select your enquiry type'}
                            onChange={({ value }) => {
                                if (Object.keys(errors)?.length > 0) {
                                    clearErrors();
                                }

                                setValue(ENQUIRY_TYPE_FIELD, value);
                            }}
                        />
                    </div>

                    {watch(ENQUIRY_TYPE_FIELD) && (
                        <>
                            <div className={'pb-4'}>
                                <TextField
                                    name={FIRST_NAME_FIELD}
                                    label={'First name'}
                                    isDisabled={isSubmitting}
                                />

                                {errors[FIRST_NAME_FIELD] && <ErrorMessage field={FIRST_NAME_FIELD}/>}
                            </div>

                            <div className={'pb-4'}>
                                <TextField
                                    name={LAST_NAME_FIELD}
                                    label={'Last name'}
                                    isDisabled={isSubmitting}
                                />
                                {errors[LAST_NAME_FIELD] && <ErrorMessage field={LAST_NAME_FIELD}/>}
                            </div>

                            <div className={'pb-4'}>
                                <TextField
                                    name={EMAIL_FIELD}
                                    label={'Email'}
                                    type={'email'}
                                    isDisabled={isSubmitting}
                                />

                                {errors[EMAIL_FIELD] && <ErrorMessage field={EMAIL_FIELD}/>}
                            </div>

                            <div className={'pb-4'}>
                                <PhoneNumberField
                                    label={'Phone'}
                                    name={PHONE_NUMBER_FIELD}
                                    disabled={isSubmitting}
                                />
                                {errors[PHONE_NUMBER_FIELD] && <ErrorMessage field={PHONE_NUMBER_FIELD}/>}
                            </div>

                            <div className={'custom-radio-options pb-4'}>
                                <FieldLabel name={PREFERRED_CONTACT_METHOD_FIELD}
                                    label={'How would you like us to contact you?'}/>
                                <div className={'d-flex align-items-center'}>
                                    {[{ name: 'Email', value: 'email' }, { name: 'Phone', value: 'phone' }].map((item, idx) => (
                                        <RadioOptionField
                                            key={idx}
                                            control={control}
                                            name={PREFERRED_CONTACT_METHOD_FIELD}
                                            value={item.value}
                                            isChecked={watch(PREFERRED_CONTACT_METHOD_FIELD) === item.value}
                                            label={<div className={'option-text'}>{item.name}</div>}
                                        />
                                    ))}
                                </div>
                                {errors[PREFERRED_CONTACT_METHOD_FIELD]
                                    && <ErrorMessage field={PREFERRED_CONTACT_METHOD_FIELD}/>}
                            </div>

                            <div className={'pb-4'}>
                                <DropdownField
                                    label={'What is your nearest branch?'}
                                    name={NEAREST_LOCATION_FIELD}
                                    options={nearestLocation}
                                />
                                {errors[NEAREST_LOCATION_FIELD]
                                    && <ErrorMessage field={NEAREST_LOCATION_FIELD}/>}
                            </div>

                            {isGeneralEnquiry() === false && (
                                <>
                                    <div className={'pb-4'}>
                                        <DropdownField
                                            label={'How would you like to invest?'}
                                            options={howToInvest}
                                            name={INVESTMENT_METHOD_FIELD}
                                        />
                                        {errors[INVESTMENT_METHOD_FIELD]
                                            && <ErrorMessage field={INVESTMENT_METHOD_FIELD}/>}
                                    </div>

                                    <div className={'pb-4'}>
                                        <DropdownField
                                            label={'Have you invested before?'}
                                            options={investedBefore}
                                            name={INVESTED_BEFORE_FIELD}
                                        />
                                        {errors[INVESTED_BEFORE_FIELD] && <ErrorMessage field={INVESTED_BEFORE_FIELD}/>}
                                    </div>

                                    <div className={'pb-4'}>
                                        <DropdownField
                                            label={'How much are you considering to invest?'}
                                            options={howMuchToInvest}
                                            name={INVESTMENT_AMOUNT_FIELD}
                                        />
                                        <p className={'sub message mb-1'}>
                                            This information will help us put you in touch with the right team
                                        </p>
                                        {errors[INVESTMENT_AMOUNT_FIELD]
                                            && <ErrorMessage field={INVESTMENT_AMOUNT_FIELD}/>}
                                    </div>
                                </>
                            )}

                            <FieldLabel label={'Message (optional)'}/>
                            <textarea rows={5} {...register(MESSAGE_FIELD)} />

                            <Turnstile
                                sitekey={process.env.TURNSTILE_KEY}
                                executution="execute"
                                appearance={'interaction-only'}
                                onVerify={(token) => {
                                    try {
                                        setTurnstileToken(token);
                                    } catch (error) {
                                        turnstile.reset();
                                    }
                                }}
                            />

                            <p className={'privacy-statement mb-3'}>
                                We treat your personal data with care. By submitting your details you're agreeing to
                                our <a
                                    href={'https://www.forsythbarr.co.nz/home/privacy-policy/'}>Privacy Policy</a>.
                            </p>

                            {submitError && (
                                <div className={'mb-4'}>
                                    <FieldErrorMessage
                                        message={'There has been an error submitting the form. Please try again.'}/>
                                </div>
                            )}

                            <div>
                                <Button
                                    aria-label={'submit'}
                                    className={'mt-2 display-block position-relative'}
                                    disabled={isSubmitting}
                                    type={'submit'}
                                >Submit {isSubmitting && <LoadingNotch/>} </Button>
                            </div>
                        </>
                    )}
                </form>
            </FormProvider>
        </>
    );
};

export default EnquiryForm;
